import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { getFriday } from '#app/models/classes.server'

export async function loader({ request }: LoaderFunctionArgs) {
	const friday = await getFriday()
	const url = new URL(request.url)
	const searchParams = new URLSearchParams(url.search)
	const classSortBy = searchParams.get('class')
	const ageSortBy = searchParams.get('age')

	let fridaytFiltered = {
		yellow: friday.filter((m) => m.room.toLowerCase() === 'yellow'),
		blue: friday.filter((m) => m.room.toLowerCase() === 'blue'),
		pink: friday.filter((m) => m.room.toLowerCase() === 'pink'),
		purple: friday.filter((m) => m.room.toLowerCase() === 'purple'),
	}

	if (classSortBy && classSortBy !== 'all') {
		fridaytFiltered = {
			yellow: fridaytFiltered.yellow.filter(
				(m) => m.classSort.toLowerCase() === classSortBy,
			),
			blue: fridaytFiltered.blue.filter(
				(m) => m.classSort.toLowerCase() === classSortBy,
			),
			pink: fridaytFiltered.pink.filter(
				(m) => m.classSort.toLowerCase() === classSortBy,
			),
			purple: fridaytFiltered.purple.filter(
				(m) => m.classSort.toLowerCase() === classSortBy,
			),
		}
	}

	if (ageSortBy && ageSortBy !== 'all') {
		fridaytFiltered = {
			yellow: fridaytFiltered.yellow.filter(
				(m) => m.ageSort.toLowerCase() === ageSortBy,
			),
			blue: fridaytFiltered.blue.filter(
				(m) => m.ageSort.toLowerCase() === ageSortBy,
			),
			pink: fridaytFiltered.pink.filter(
				(m) => m.ageSort.toLowerCase() === ageSortBy,
			),
			purple: fridaytFiltered.purple.filter(
				(m) => m.ageSort.toLowerCase() === ageSortBy,
			),
		}
	}

	return json({
		fridaytFiltered,
	})
}

export default function MondayClasses() {
	const { fridaytFiltered } = useLoaderData<typeof loader>()
	return (
		<div className="text-stone-50">
			<div className="mt-6 grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 xl:grid-cols-4">
				<div className="divide-y-2 divide-[#f9c04d]">
					<h2 className="mt-2 px-4 py-2 text-center text-xl font-bold tracking-widest text-white">
						Yellow Room
					</h2>
					<ul className="grid grid-cols-1 divide-y divide-gray-600 rounded-lg bg-stone-800 shadow-emerald-200">
						{fridaytFiltered.yellow.length === 0 ? (
							<li className="col-span-1 flex px-6 py-8 text-sm">
								No classes match filter on Friday for this room.
							</li>
						) : (
							fridaytFiltered.yellow.map((y) => (
								<li key={y.class}>
									<div className="col-span-1 flex px-6 py-4">
										<div className="flex w-36 flex-shrink-0 items-start justify-start text-sm uppercase leading-5 text-white">
											{y.time}
										</div>
										<div className="flex flex-col items-start justify-between gap-y-2">
											<div className="flex-1 text-sm leading-5">
												<div className="text-base font-bold text-white">
													{y.class}
												</div>
												<div className="text-[#f9c04d]">{y.age}</div>
											</div>
											<div className="flex-1 text-xs leading-5">
												<div className="flex gap-x-2">
													<div className="rounded bg-stone-700 px-1 text-stone-300">
														{y.length}
													</div>
													{y.recital && (
														<div className="rounded bg-stone-700 px-1 text-stone-300">
															{y.recital}
														</div>
													)}
												</div>
												<div className="pt-2">{y.required}</div>
											</div>
										</div>
									</div>
								</li>
							))
						)}
					</ul>
				</div>
				<div className="divide-y-2 divide-blue-500">
					<h2 className="mt-2 px-4 py-2 text-center text-xl font-bold tracking-widest text-white">
						Blue Room
					</h2>
					<ul className="grid grid-cols-1 divide-y divide-gray-600 rounded-lg bg-stone-800">
						{fridaytFiltered.blue.length === 0 ? (
							<li className="col-span-1 flex px-6 py-8 text-sm">
								No classes match filter on Friday for this room.
							</li>
						) : (
							fridaytFiltered.blue.map((b) => (
								<li key={b.class}>
									<div className="col-span-1 flex px-6 py-4">
										<div className="flex w-36 flex-shrink-0 items-start justify-start text-sm uppercase leading-5 text-white">
											{b.time}
										</div>
										<div className="flex flex-col items-start justify-between gap-y-2">
											<div className="flex-1 text-sm leading-5">
												<div className="text-base font-bold text-white">
													{b.class}
												</div>
												<div className="text-[#f9c04d]">{b.age}</div>
											</div>
											<div className="flex-1 text-xs leading-5">
												<div className="flex gap-x-2">
													<div className="rounded bg-stone-700 px-1 text-stone-300">
														{b.length}
													</div>
													{b.recital && (
														<div className="rounded bg-stone-700 px-1 text-stone-300">
															{b.recital}
														</div>
													)}
												</div>
												<div className="pt-2">{b.required}</div>
											</div>
										</div>
									</div>
								</li>
							))
						)}
					</ul>
				</div>
				<div className="divide-y-2 divide-pink-500">
					<h2 className="mt-2 px-4 py-2 text-center text-xl font-bold tracking-widest text-white">
						Pink Room
					</h2>
					<ul className="grid grid-cols-1 divide-y divide-gray-600 rounded-lg bg-stone-800">
						{fridaytFiltered.pink.length === 0 ? (
							<li className="col-span-1 flex px-6 py-8 text-sm">
								No classes match filter on Friday for this room.
							</li>
						) : (
							fridaytFiltered.pink.map((p) => (
								<li key={p.class}>
									<div className="col-span-1 flex px-6 py-4">
										<div className="flex w-36 flex-shrink-0 items-start justify-start text-sm uppercase leading-5 text-white">
											{p.time}
										</div>
										<div className="flex flex-col items-start justify-between gap-y-2">
											<div className="flex-1 text-sm leading-5">
												<div className="text-base font-bold text-white">
													{p.class}
												</div>
												<div className="text-[#f9c04d]">{p.age}</div>
											</div>
											<div className="flex-1 text-xs leading-5">
												<div className="flex gap-x-2">
													<div className="rounded bg-stone-700 px-1 text-stone-300">
														{p.length}
													</div>
													{p.recital && (
														<div className="rounded bg-stone-700 px-1 text-stone-300">
															{p.recital}
														</div>
													)}
												</div>
												<div className="pt-2">{p.required}</div>
											</div>
										</div>
									</div>
								</li>
							))
						)}
					</ul>
				</div>
				<div className="divide-y-2 divide-purple-500">
					<h2 className="mt-2 px-4 py-2 text-center text-xl font-bold tracking-widest text-white">
						Purple Room
					</h2>
					<ul className="grid grid-cols-1 divide-y divide-gray-600 rounded-lg bg-stone-800">
						{fridaytFiltered.purple.length === 0 ? (
							<li className="col-span-1 flex px-6 py-8 text-sm">
								No classes match filter on Friday for this room.
							</li>
						) : (
							fridaytFiltered.purple.map((p) => (
								<li key={p.class}>
									<div className="col-span-1 flex px-6 py-4">
										<div className="flex w-36 flex-shrink-0 items-start justify-start text-sm uppercase leading-5 text-white">
											{p.time}
										</div>
										<div className="flex flex-col items-start justify-between gap-y-2">
											<div className="flex-1 text-sm leading-5">
												<div className="text-base font-bold text-white">
													{p.class}
												</div>
												<div className="text-[#f9c04d]">{p.age}</div>
											</div>
											<div className="flex-1 text-xs leading-5">
												<div className="flex gap-x-2">
													<div className="rounded bg-stone-700 px-1 text-stone-300">
														{p.length}
													</div>
													{p.recital && (
														<div className="rounded bg-stone-700 px-1 text-stone-300">
															{p.recital}
														</div>
													)}
												</div>
												<div className="pt-2">{p.required}</div>
											</div>
										</div>
									</div>
								</li>
							))
						)}
					</ul>
				</div>
			</div>
		</div>
	)
}
